import {ErrorMessage, Form, Formik} from 'formik'
import {useEffect, useState} from 'react'
import {useQueryClient} from 'react-query'
import Modal from 'rsuite/Modal'
import FormRadio from 'src/cms/helpers/components/forms/FormRadio'
import FormSelect from 'src/cms/helpers/components/forms/FormSelect'
import FormTextBox from 'src/cms/helpers/components/forms/FormTextBox'
import * as Yup from 'yup'
import {
  useAddDocuments,
  useGetDocuments,
  useGetDocumentsById,
  useUpdateDocuments,
} from './hooks/useGetDocuments'

type Props = {
  open: boolean
  handleClose: () => void
  id: string
}

const AddDocuments = ({open, handleClose, id}: Props) => {
  const queryClient = useQueryClient()
  const [isUpdating, setIsUpdating] = useState(false)
  const {data: productSchemaByIdData} = useGetDocumentsById(id)
  const addDocumentMutation = useAddDocuments()
  const updateDocumentMutation = useUpdateDocuments()

  const statusOptions = [
    {label: 'Active', value: 'Active'},
    {label: 'Inactive', value: 'Inactive'},
  ]
  const yesNoOptions = [
    {label: 'YES', value: 'Yes'},
    {label: 'NO', value: 'No'},
  ]

  const FORM_VALIDATION = Yup.object({
    title: Yup.string()
      .required('Required')
      .matches(/^[A-Za-z\s]+$/, 'Only letters are allowed'),
    order: Yup.number()
      .min(1, 'Order must be greater than 0')
      .max(99, 'Order must be less than 99')
      .required('Required'),
  })

  // Map API data to form fields
  const getInitialValues = () => {
    const data = productSchemaByIdData?.data
    return {
      ...data,
      title: data?.title || '',
      is_account_opening_from_nepal: data?.is_account_opening_from_nepal ? 'Yes' : 'No',
      is_nepalese_citizen_working_overseas: data?.is_nepalese_citizen_working_overseas
        ? 'Yes'
        : 'No',
      is_nrn: data?.is_nrn ? 'Yes' : 'No',
      status: data?.status ? 'Active' : 'Inactive',
      order: data?.order || 0,
    }
  }

  async function handleSubmit(value: any) {
    setIsUpdating(true)
    const updateValue = {
      ...value,
      is_account_opening_from_nepal: value?.is_account_opening_from_nepal === 'Yes',
      is_nepalese_citizen_working_overseas: value?.is_nepalese_citizen_working_overseas === 'Yes',
      is_nrn: value?.is_nrn === 'Yes',
      status: value?.status === 'Active',
      order: value?.order || 0,
    }

    try {
      if (value?.id) {
        await updateDocumentMutation.mutateAsync(updateValue)
      } else {
        await addDocumentMutation.mutateAsync(updateValue)
      }
      // If the mutation succeeded, hooks already invalidate queries
      queryClient.refetchQueries('GET_DOCUMENTS')
      handleClose()
    } catch (error: any) {
      console.error(error)
    } finally {
      setIsUpdating(false)
    }
  }

  return (
    <div className='modal-container'>
      <Modal
        open={open}
        onClose={handleClose}
        className='w-75'
        backdrop='static'
        enforceFocus={false}
        keyboard={false}
        data-cy='modal'
      >
        <Modal.Header>
          <Modal.Title data-cy='modal-title'>{id ? 'Edit' : 'Add'} Documents</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            <Formik
              initialValues={getInitialValues()}
              validationSchema={FORM_VALIDATION}
              onSubmit={(value) => handleSubmit(value)}
              enableReinitialize
            >
              {({touched, handleChange, errors, values, setFieldValue}) => {
                return (
                  <Form>
                    <div className='card-body border-top pt-5'>
                      <div className='row'>
                        <div className='col-md-6 col-xs-12'>
                          <FormTextBox
                            type='text'
                            placeholder='Enter Title'
                            name='title'
                            label='Title'
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            value={values.title}
                            required={true}
                          />
                        </div>

                        <div className='col-md-6 col-xs-12'>
                          <FormSelect
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            options={yesNoOptions}
                            name='is_account_opening_from_nepal'
                            label='Account Opening From Nepal'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            values={values}
                            setFieldValue={setFieldValue}
                            required={true}
                          />
                        </div>
                        <div className='col-md-6 col-xs-12'>
                          <FormSelect
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            options={yesNoOptions}
                            name='is_nepalese_citizen_working_overseas'
                            label='Nepalese Citizen Working Overseas'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            values={values}
                            setFieldValue={setFieldValue}
                            required={true}
                          />
                        </div>
                        <div className='col-md-6 col-xs-12'>
                          <FormSelect
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            options={yesNoOptions}
                            name='is_nrn'
                            label='NRN'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            values={values}
                            setFieldValue={setFieldValue}
                            required={true}
                          />
                        </div>
                        <div className='col-md-6 col-xs-12'>
                          <FormTextBox
                            type='number'
                            placeholder='Enter Order'
                            name='order'
                            label='Order'
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            value={values.order}
                            required={true}
                            maxLength={2}
                          />
                        </div>
                      </div>

                      <div className='row'>
                        <div className='col-md-6 col-xs-12'>
                          <label className='form-label fw-bolder text-dark fs-6 required'>
                            Status
                          </label>
                          <div className='d-flex ms-5'>
                            {statusOptions?.map((status: {label: string; value: string}) => (
                              <FormRadio
                                key={status.value}
                                containerClassName=''
                                label='Select Status'
                                name='status'
                                type='radio'
                                onChange={handleChange}
                                errors={errors}
                                touched={touched}
                                required={true}
                                checkBoxText={status?.label}
                                value={status?.value}
                                checked={values.status === status.value}
                                data-cy='product-category-status-radio'
                              />
                            ))}
                          </div>
                          <div className='fv-plugins-message-container text-danger fw-bolder small text-end mt-1'>
                            <ErrorMessage
                              name='status'
                              component='div'
                              className='field-error-message'
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className='d-flex justify-content-end px-9 '>
                      <button
                        type='submit'
                        disabled={isUpdating}
                        className='btn btn-primary btn-sm ms-3'
                        data-cy='modal-submit'
                      >
                        {isUpdating ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        type='button'
                        onClick={handleClose}
                        className='btn btn-secondary btn-sm ms-3'
                        data-cy='modal-cancel'
                      >
                        Cancel
                      </button>
                    </div>
                  </Form>
                )
              }}
            </Formik>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  )
}
export default AddDocuments
